import { Popover } from "@headlessui/react";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import React from "react";
import { useState } from "react";
import { DateRangePicker as DRP } from "react-date-range";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file

import { usePopper } from "react-popper";
import Card from "../Card";

const DateRangePicker = ({ onDateSelect, defaultRange }) => {
  const [selectedRange, setSelectedRange] = useState(
    defaultRange ?? {
      startDate: new Date(moment().subtract(30, "days").format("YYYY-MM-DD")),
      endDate: new Date(moment().format("YYYY-MM-DD")),
      key: "selection",
    }
  );
  const checkDateRangeSelected = (start, end) => {
    return (
      moment(start).format("YYYY-MM-DD") ===
        moment(selectedRange.startDate).format("YYYY-MM-DD") &&
      moment(end).format("YYYY-MM-DD") ===
        moment(selectedRange.endDate).format("YYYY-MM-DD")
    );
  };
  const datePickerRanges = [
    {
      label: "Last 7 Days",
      range: () => ({
        startDate: new Date(moment().subtract(7, "days").format("YYYY-MM-DD")),
        endDate: new Date(moment().format("YYYY-MM-DD")),
      }),
      isSelected: () =>
        checkDateRangeSelected(moment().subtract(7, "days"), moment()),
    },

    {
      label: "Last 14 Days",
      range: () => ({
        startDate: new Date(
          moment().subtract(14, "days").startOf("day").format("YYYY-MM-DD")
        ),
        endDate: new Date(),
      }),
      isSelected: () =>
        checkDateRangeSelected(
          moment().subtract(14, "days").startOf("day"),
          moment()
        ),
    },
    {
      label: "Last 30 Days",
      range: () => ({
        startDate: new Date(
          moment().subtract(30, "days").startOf("day").format("YYYY-MM-DD")
        ),
        endDate: new Date(),
      }),
      isSelected: () =>
        checkDateRangeSelected(
          moment().subtract(30, "days").startOf("day"),
          moment()
        ),
    },
    {
      label: "This Month",
      range: () => ({
        startDate: new Date(moment().startOf("month").format("YYYY-MM-DD")),
        endDate: new Date(),
      }),
      isSelected: () =>
        checkDateRangeSelected(moment().startOf("month"), moment()),
    },
    {
      label: "Last Month",
      range: () => ({
        startDate: new Date(
          moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD")
        ),
        endDate: new Date(
          moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD")
        ),
      }),
      isSelected: () =>
        checkDateRangeSelected(
          moment().subtract(1, "month").startOf("month"),
          moment().subtract(1, "month").endOf("month")
        ),
    },
    {
      label: "All Time",
      range: () => ({
        startDate: new Date("2023-01-01"),
        endDate: new Date(),
      }),
      isSelected: () => checkDateRangeSelected(moment("2023-01-01"), moment()),
    },
  ];

  let [referenceElement, setReferenceElement] = useState();
  let [popperElement, setPopperElement] = useState();
  let { styles, attributes } = usePopper(referenceElement, popperElement);

  const handleSelect = (ranges) => {
    setSelectedRange(ranges.selection);
    onDateSelect({
      start: moment(ranges.selection.startDate).format("YYYY-MM-DD"),
      end: moment(ranges.selection.endDate).format("YYYY-MM-DD"),
    });
  };

  return (
    <Popover className="relative">
      <Popover.Button
        ref={setReferenceElement}
        className={
          "bg-primary px-4 py-2 border rounded-lg text-white font-semibold flex items-center space-x-4"
        }
      >
        <CalendarDaysIcon className="h-6 w-6" />
        <p>
          {moment(selectedRange.startDate).format("YYYY.MM.DD") +
            " - " +
            moment(selectedRange.endDate).format("YYYY.MM.DD")}
        </p>
      </Popover.Button>

      <Popover.Panel
        className="z-50"
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
      >
        <style>
          {`.rdrStaticRange{margin-right:12px}
            .rdrStaticRange.rdrStaticRangeSelected > span{color:#A87BCC}
            .rdrDefinedRangesWrapper{border-style:none; color: #111827}
            .rdrMonthAndYearPickers{font-weight:700; font-size:15px;}
            .rdrMonth{padding:0}
            .rdrWeekDays{background-color: #F2F4FB; margin-bottom:5px}
            .rdrWeekDay{font-weight:500; color:#111827}
            .rdrEndEdge{background-color: #A87BCC; color:#ffffff}
            .rdrStartEdge{background-color: #A87BCC;}
            .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span{color:#ffffff; font-weight:500;}
            .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span{color:#ffffff; font-weight:500;}
            .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span{color:#A87BCC; font-weight:500}`}
        </style>
        <Card>
          <DRP
            className=""
            ranges={[selectedRange]}
            onChange={handleSelect}
            minDate={new Date("2023-01-01")}
            staticRanges={datePickerRanges}
            inputRanges={[]}
            showSelectionPreview={true}
            weekStartsOn={1}
            showMonthAndYearPickers={true}
            maxDate={new Date()}
            showDateDisplay={false}
            rangeColors={["#DCE8FF"]}
          />
        </Card>
      </Popover.Panel>
    </Popover>
  );
};

export default DateRangePicker;
